import React from 'react';
import get from 'lodash.get';
import { useNavigate, useLocation } from 'react-router-dom';
import { useActions } from 'hooks/useActions';
import { useSelector } from 'react-redux';
import {
  FlexBox,
  Box,
  Typography,
  Button,
  SvgIconButton,
  Grid,
  MuiBox,
  MuiTable,
  SvgIcon
} from '@kis-technologies/kis-ui';
import { selectors as configSelectors, Configuration } from 'services/configuration';
import { selectors as kioskSelectors, KioskHardware } from 'services/kiosk';
import { actions as ticketingActions, selectors as ticketingSelectors } from 'services/ticketing';
import { selectors as hardwareSelectors, actions as hardwareActions, Scanner } from 'services/hardware';
import { actions as cityPassActions, selectors as cityPassSelectors, CityPassTicket } from 'services/cityPass';
import { ERROR, USER_ERROR, NOT_CONNECTED, OPERATIONAL } from 'constants/hardware';
import { Footer } from 'components/Footer';
import { Scrollbar } from 'components/Scrollbar';
import { ActionOverlay } from 'components/ActionOverlay';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { ProcessingOverlay } from 'components/ProcessingOverlay';
import BarcodeScan from 'assets/barcode_scan.png';
import { useStyles } from './styled';
import { useTranslation } from 'react-i18next';
import { BarcodeKeyEntry } from './components/barcodeKeyEntry';

const CityPassScan: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // Actions and Selectors
  const { createSession, setConfigPath } = useActions(ticketingActions);
  const { scannerRead, resetScanner } = useActions(hardwareActions);
  const { validateTicket, removeTicket, resetCityPass, resetAction } = useActions(cityPassActions);
  const { status }: Scanner = useSelector(hardwareSelectors.selectScanner);
  const session = useSelector(ticketingSelectors.selectSession);
  const cityPassTickets = useSelector(cityPassSelectors.selectTickets) || [];
  const { success, loading, error: cpError } = useSelector(cityPassSelectors.selectAction);
  const sessionLoading = useSelector(ticketingSelectors.selectLoading);
  const kiosk = useSelector(kioskSelectors.selectKiosk);
  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;

  const isPreviewMode = sessionStorage.getItem('mode') === 'preview';
  const fontFamily = get(configuration, 'configuration.globalSettings.fonts.primary');
  const { passLimit = 10, scanning = null, manualEntry = null } = get(configuration, 'cityPass.ticketEntry') || {};
  const scanPageHeader = get(scanning, `messages.header.${i18n.language}`) || t('cityPass.scanHeader');
  const scanPageSubheader =
    get(scanning, `messages.subheader.${i18n.language}`) || t('cityPass.scanDescription', { passLimit });
  const scanPageTypeButton = get(scanning, `messages.buttons.${i18n.language}[0]`) || t('cityPass.typeBarcode');
  const isManualEntryEnabled = get(manualEntry, 'enabled');

  const [error, setError] = React.useState('');
  const [scannerError, setScannerError] = React.useState(false);
  const [keyedValue, setKeyedValue] = React.useState('');
  const [barcodeKeyEntry, setBarcodeKeyEntry] = React.useState(false);
  const [goBackWarning, setGoBackWarning] = React.useState(false);

  const classes = useStyles({ fontFamily })();

  const checkLimitAndScan = () => {
    setKeyedValue('');
    if (!sessionLoading && cityPassTickets.length < passLimit && !isPreviewMode) {
      scannerRead();
    }
  };

  const handleBarcodeSubmit = (barCode: string) => {
    const trimmedCode = barCode.replace(/\s+/g, '');
    if (cityPassTickets.map((item) => item.barcode).includes(trimmedCode)) {
      setError(t('cityPass.cpDupe'));
      resetScanner();
    } else {
      validateTicket(trimmedCode);
      resetScanner();
    }
  };

  // On Mount - call scanner read: hits barcode scanner hardware
  React.useEffect(() => {
    checkLimitAndScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityPassTickets]);

  React.useEffect(() => {
    if (status.message && status.code === OPERATIONAL) {
      handleBarcodeSubmit(status.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.message]);

  React.useEffect(() => {
    if (success) {
      resetAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  React.useEffect(() => {
    if ([ERROR, USER_ERROR, NOT_CONNECTED].includes(status.code) && status.rawCode) {
      if (status.rawCode === 'scanner_timeout') {
        resetScanner();
        checkLimitAndScan();
      } else {
        setScannerError(true);
      }
    } else {
      setScannerError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.rawCode]);

  React.useEffect(() => {
    if (session) {
      setConfigPath('cityPass.ticketing');
      navigate('/ticketing/genres');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const getBarcodeScanImgUrl = () => {
    if (kiosk) {
      const barcodeScanner = kiosk.hardware.find((device: KioskHardware) => device.type === 'barcodeScanner');
      if (barcodeScanner && barcodeScanner.imageUrl && barcodeScanner.imageUrl.primary) {
        return barcodeScanner.imageUrl.primary;
      }
    }
    return undefined;
  };

  const onPrevPageClick = () => {
    resetCityPass();
    // re-routes to the previous page (StartPurchaseView)
    navigate('/ticketing/start-purchase', { state: { from: location.pathname } });
  };

  const resetAndTryAgain = () => {
    if (cpError) {
      resetAction();
    } else {
      setError('');
    }
    if (keyedValue) {
      setBarcodeKeyEntry(true);
    } else {
      checkLimitAndScan();
    }
  };

  const Row = (rowProps: { row: CityPassTicket }) => {
    const { row } = rowProps;
    return (
      <MuiTable.TableRow className={classes.tableRows}>
        <MuiTable.TableCell className={`${classes.tableBodyCell} ${classes.cpLevelCell}`}>
          <Typography fontSize={24} fontWeight={600}>
            {row.className}
          </Typography>
        </MuiTable.TableCell>
        <MuiTable.TableCell className={`${classes.tableBodyCell} ${classes.cpBarcodeCell}`}>
          <Typography fontSize={24} fontWeight={600}>
            {row.barcode}
          </Typography>
        </MuiTable.TableCell>
        <MuiTable.TableCell className={`${classes.tableBodyCell} ${classes.cpRemoveCell}`}>
          <FlexBox align="center" justify="center">
            <SvgIconButton
              onClick={() => removeTicket(row.barcode)}
              appearance="transparent"
              iconColor="#FFF"
              background="rgb(224, 32, 32)"
              data-testid={'exit-icon-button'}
            >
              <SvgIcon name="exit" size={40} />
            </SvgIconButton>
          </FlexBox>
        </MuiTable.TableCell>
      </MuiTable.TableRow>
    );
  };

  if (barcodeKeyEntry) {
    return (
      <BarcodeKeyEntry
        loading={loading}
        value={keyedValue}
        onCancel={() => {
          setBarcodeKeyEntry(false);
          checkLimitAndScan();
        }}
        onSubmit={(value: string) => {
          setBarcodeKeyEntry(false);
          setKeyedValue(value);
          handleBarcodeSubmit(value);
        }}
      />
    );
  }

  return (
    <>
      {sessionLoading && <LoadingOverlay style={{ zIndex: '99' }} />}

      {!(loading || sessionLoading || error || cpError) ? (
        <>
          <Grid className={classes.cityPassOuterContainer}>
            <Grid container direction="column" className={classes.cityPassInnerContainer}>
              <MuiBox className={classes.cityPassContentArea}>
                {/* Title */}
                <MuiBox className={classes.cityPassHeaderArea}>
                  <MuiBox className={classes.cityPassHeading}>
                    <Typography variant="h2" fontSize={52} sx={{ lineHeight: 1.2 }}>
                      {scanPageHeader}
                    </Typography>
                    <Typography fontSize={24}>{scanPageSubheader}</Typography>
                  </MuiBox>
                  <FlexBox flexOverride="1 0 auto" align="center" justify="center">
                    {!getBarcodeScanImgUrl() && (
                      <MuiBox className={classes.imageBox}>
                        <img height={500} alt="url" src={BarcodeScan} />
                      </MuiBox>
                    )}
                    {getBarcodeScanImgUrl() && <img width={450} height={500} alt="url" src={getBarcodeScanImgUrl()} />}
                  </FlexBox>
                </MuiBox>
                {cityPassTickets.length > 0 && (
                  <MuiBox className={classes.cityPassTableArea}>
                    <Scrollbar refreshModel={cityPassTickets}>
                      <Box flexOverride="none" padding="0 3" fullHeight>
                        <MuiTable.TableContainer id="cityPassListTable">
                          <MuiTable.Table
                            className={classes.tableContainer}
                            stickyHeader
                            sx={{ borderCollapse: 'collapse' }}
                          >
                            <MuiTable.TableHead>
                              <MuiTable.TableRow className={classes.tableHeadRows}>
                                <MuiTable.TableCell className={`${classes.tableHeadCell} ${classes.cpLevelCell}`}>
                                  {t('cityPass.level')}
                                </MuiTable.TableCell>
                                <MuiTable.TableCell className={`${classes.tableHeadCell} ${classes.cpBarcodeCell}`}>
                                  {t('cityPass.barcode')}
                                </MuiTable.TableCell>
                                <MuiTable.TableCell className={`${classes.tableHeadCell} ${classes.cpRemoveHeadCell}`}>
                                  {t('cart.remove')}
                                </MuiTable.TableCell>
                              </MuiTable.TableRow>
                            </MuiTable.TableHead>
                            <MuiTable.TableBody>
                              {cityPassTickets.map((row: CityPassTicket, idx: number) => {
                                return <Row key={idx} row={row} />;
                              })}
                            </MuiTable.TableBody>
                          </MuiTable.Table>
                        </MuiTable.TableContainer>
                      </Box>
                    </Scrollbar>
                  </MuiBox>
                )}
                {isManualEntryEnabled && cityPassTickets.length < passLimit && (
                  <MuiBox className={classes.cpBottomArea}>
                    {/* Component will display only in portrait mode*/}
                    <MuiBox className={classes.cpFooterBtnsPortrait}>
                      <Button
                        onClick={() => setBarcodeKeyEntry(true)}
                        appearance="white"
                        customSize="x-large"
                        data-testid="type-code-button"
                      >
                        {scanPageTypeButton}
                      </Button>
                    </MuiBox>
                  </MuiBox>
                )}
              </MuiBox>
            </Grid>
          </Grid>
          {/* Footer */}
          <Footer
            onPrevClick={() => {
              if (cityPassTickets.length) setGoBackWarning(true);
              else onPrevPageClick();
            }}
            onNextClick={() => createSession()}
            prevText={t('commonText.back')}
            nextText={t('commonText.continue')}
            isNextDisabled={!cityPassTickets.length}
            skipNavPrompt={Boolean(scannerError || cpError || error || goBackWarning)}
          >
            {isManualEntryEnabled && cityPassTickets.length < passLimit && (
              <MuiBox className={classes.footerMidddleBtn}>
                <Button
                  onClick={() => setBarcodeKeyEntry(true)}
                  appearance="white"
                  customSize="x-large"
                  data-testid="type-code-button"
                >
                  {scanPageTypeButton}
                </Button>
              </MuiBox>
            )}
          </Footer>
        </>
      ) : null}

      {(loading || success) && (
        <ProcessingOverlay
          error={error || cpError}
          processed={success}
          processAction={() => {}}
          title={t('processing.processingTitle')}
        />
      )}

      {scannerError && !loading && (
        <ActionOverlay
          title={
            <Box margin="0 0 5" width={710}>
              <Typography fontSize={64} fontWeight={600} align="center" sx={{ lineHeight: 1 }}>
                {t('commonText.somethingWentWrong')}
              </Typography>
            </Box>
          }
          confirmText={t('commonText.ok')}
          onConfirm={checkLimitAndScan}
          cancelText={t('commonText.back')}
          onCancel={onPrevPageClick}
        />
      )}

      {/* Error Overlay */}
      {(cpError || error) && (
        <ActionOverlay
          title={
            <Typography fontSize={48} fontWeight={600} align="center" sx={{ lineHeight: 1.2 }}>
              {cpError || error}
            </Typography>
          }
          height={500}
          confirmText={t('commonText.ok')}
          onConfirm={resetAndTryAgain}
          style={{ zIndex: '2147483648' }}
        />
      )}

      {goBackWarning && (
        <ActionOverlay
          title={
            <Typography variant="h2" align="center" sx={{ lineHeight: 1 }}>
              {t('commonText.goBackTitle')}
            </Typography>
          }
          description={
            <Typography align="center" fontSize={30}>
              {t('cityPass.goBackWarning')}
            </Typography>
          }
          height={500}
          cancelText={t('commonText.goBack')}
          confirmText={t('commonText.close')}
          onCancel={onPrevPageClick}
          onConfirm={() => setGoBackWarning(false)}
        />
      )}
    </>
  );
};

export default CityPassScan;
